import { useEffect, useRef, useState } from 'react';
import { useLang } from '../../i18n/translations';
import { cn } from '../../lib/utils';

interface Props {
  lastUpdated: Date | null;
  intervalSec: number;
  className?: string;
}

export function SyncIndicator({ lastUpdated, intervalSec, className }: Props) {
  const { t } = useLang();
  const [, setTick] = useState(0);
  const [flash, setFlash] = useState(false);
  const prevTs = useRef<number | null>(null);

  useEffect(() => {
    const id = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const ts = lastUpdated?.getTime() ?? null;
    if (ts !== null && prevTs.current !== null && ts !== prevTs.current) {
      setFlash(true);
      const id = window.setTimeout(() => setFlash(false), 600);
      prevTs.current = ts;
      return () => window.clearTimeout(id);
    }
    prevTs.current = ts;
  }, [lastUpdated]);

  const secondsAgo = lastUpdated
    ? Math.max(0, Math.floor((Date.now() - lastUpdated.getTime()) / 1000))
    : null;
  const agoLabel =
    secondsAgo === null ? '…'
    : secondsAgo < 5 ? t.sync.now
    : secondsAgo < 60 ? t.sync.agoSec(secondsAgo)
    : t.sync.agoMin(Math.floor(secondsAgo / 60));

  return (
    <span
      title={t.sync.title(intervalSec)}
      className={cn(
        'inline-flex items-center gap-1.5 text-[11px] font-data transition-colors duration-300',
        flash ? 'text-brand' : 'text-ink-faint',
        className,
      )}
    >
      <span className={cn(
        'inline-flex h-1.5 w-1.5 rounded-full shrink-0 transition-colors',
        flash ? 'bg-brand' : 'bg-brand/50',
      )} />
      {t.sync.live} · {agoLabel}
    </span>
  );
}
